/// <amd-module name="Artifacts/Connect/tools/Upack" />
import { CopyableCode, DetailsCard, PublishDetailsCard } from "Artifacts/Connect/components/Common";
import { OperatingSystem } from "Artifacts/Connect/Constants";
import * as Res from "Artifacts/Connect/Resources";
import { PackagingService, TfsService } from "Artifacts/Connect/services/PackagingService";
import { ToolService } from "Artifacts/Connect/services/ToolService";
import { IToolConnectProps } from "Artifacts/Connect/tools/IToolConnectProps";
import { ICombinedFeedView } from "Artifacts/Connect/types/ICombinedFeedView";
import { IProtocol } from "Artifacts/Connect/types/IProtocol";
import { ITool, ToolId } from "Artifacts/Connect/types/ITool";
import { FWLink } from "Artifacts/Root/components/FWLink";
import * as React from "react";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { IPageData } from "VSS/Platform/Page";
import { Link } from "VSSUI/Link";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";

const UPackConnectComponent: React.FC<IToolConnectProps<UPackProtocol>> = ({
  feed,
  packagingService,
  tfsService,
}) => {
  const orgUrl = packagingService.getUrl().replace(/([\W])pkgs\./, "$1");
  const tfsServiceUrl = tfsService.getSessionTokenUri();
  const [tabId, setTabId] = React.useState<string>(OperatingSystem.Windows);

  return (
    <React.Fragment>
      {feed.project && (
        <MessageCard severity={MessageCardSeverity.Warning}>
          <FormatComponent format={Res.UPack_AzureCLI}>
            <FWLink underline linkId="2110647">
              {Res.Prerequisites}
            </FWLink>
          </FormatComponent>
        </MessageCard>
      )}

      <DetailsCard title={{ text: Res.ProjectSetup, ariaLevel: 3 }}>
        <TabBar onSelectedTabChanged={setTabId} selectedTabId={tabId} tabSize={TabSize.Compact}>
          <Tab
            name="Windows"
            id={OperatingSystem.Windows}
            iconProps={{ iconName: "WindowsLogo" }}
          />
          <Tab name={Res.Other} id={OperatingSystem.Other} iconProps={{ iconName: "PC1" }} />
        </TabBar>

        {tabId === OperatingSystem.Windows ? (
          <React.Fragment>
            <p className="tooldetails-smaller-margin">{Res.UPack_AzLogin}</p>

            <CopyableCode ariaLabel={Res.ProjectSetup}>az login</CopyableCode>

            <p className="body-s">
              <FormatComponent format={Res.PAT_Issues}>
                <Link underline className="body-s" onClick={() => setTabId(OperatingSystem.Other)}>
                  {Res.PAT_Instructions}
                </Link>
              </FormatComponent>
            </p>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <p className="tooldetails-smaller-margin">
              <FormatComponent format={Res.GeneratePAT}>
                <Link underline href={tfsServiceUrl} target="_blank">
                  {Res.PersonalAccessToken}
                </Link>
                <em>{Res.Packaging}</em>
              </FormatComponent>
            </p>
            <p>{Res.UPack_ADO_Login}</p>
            <CopyableCode ariaLabel={Res.ProjectSetup}>
              az devops login --organization {orgUrl}
            </CopyableCode>
          </React.Fragment>
        )}
      </DetailsCard>

      <DetailsCard title={{ text: Res.DownloadPackages, ariaLevel: 3 }}>
        <p>{Res.UPack_Download}</p>
        <CopyableCode
          ariaLabel={Res.DownloadPackages}
          text={`az artifacts universal download --organization ${orgUrl}${getProjectRelatedArgs(
            false,
          )} --feed ${feed.name} --name ${Res.MyFirstPackage} --version 0.0.1 --path .`}
        >
          <pre>
            {`az artifacts universal download
    --organization ${orgUrl}${getProjectRelatedArgs(true)}
    --feed ${feed.name}
    --name ${Res.MyFirstPackage}
    --version 0.0.1
    --path .`}
          </pre>
        </CopyableCode>
      </DetailsCard>

      <PublishDetailsCard feed={feed}>
        <p>{Res.UPack_Publish}</p>
        <CopyableCode
          ariaLabel={Res.PublishPackages}
          text={`az artifacts universal publish --organization ${orgUrl}${getProjectRelatedArgs(
            false,
          )} --feed ${feed.name} --name ${Res.MyFirstPackage} --version 0.0.1 --description "${
            Res.Upack_Welcome
          }" --path .`}
        >
          <pre>
            {`az artifacts universal publish
    --organization ${orgUrl}${getProjectRelatedArgs(true)}
    --feed ${feed.name}
    --name ${Res.MyFirstPackage}
    --version 0.0.1
    --description "${Res.Upack_Welcome}"
    --path .`}
          </pre>
        </CopyableCode>
      </PublishDetailsCard>
    </React.Fragment>
  );

  function getProjectRelatedArgs(lineBreak: boolean) {
    if (!feed.project) {
      return "";
    }
    return lineBreak
      ? `
    --project="${feed.project.name}"
    --scope project`
      : ` --project="${feed.project.name}" --scope project`;
  }
};

class UPackProtocol implements IProtocol {
  readonly name = "Universal";
  public readonly area = "upack"; // ?

  public getFeedUrl(
    packagingService: PackagingService,
    combinedFeedView: ICombinedFeedView,
  ): string {
    // shouldn't be called
    return "";
  }
}

export class UPackTool implements ITool {
  readonly id = ToolId.UniversalPackages;
  readonly name = "Universal Packages";
  readonly protocol = new UPackProtocol();
  readonly allowNonMembers = false;
  readonly imagePath = `ms.azure-artifacts/connect/img/universal-package.svg`;
  readonly Component: React.ComponentType = UPackConnectComponent;
  readonly toolLinkId = 2103692;

  public getToolLink = () => {
    return {
      href: `https://go.microsoft.com/fwlink/?linkid=${this.toolLinkId}`,
      children: `${Res.CommandLineReference}`,
    };
  };

  public isProtocolEnabled(pageContext: IVssPageContext): boolean {
    return true;
  }

  public isEnvironmentSupported(pageData: IPageData): boolean {
    return pageData.isHosted;
  }

  supportsGet(): boolean {
    return true;
  }

  supportsPush(feed: ICombinedFeedView): boolean {
    return feed.canPublishPackages;
  }

  renderComponent(
    pageContext: IVssPageContext,
    feed: ICombinedFeedView,
    packagingService: PackagingService,
    tfsService: TfsService,
  ): JSX.Element {
    return (
      <UPackConnectComponent
        pageContext={pageContext}
        protocol={this.protocol}
        feed={feed}
        packagingService={packagingService}
        tfsService={tfsService}
      />
    );
  }
}

ToolService.registerTool(new UPackTool());
